import React, { useRef } from 'react';
import { flatten, throttle } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import additionalMessages from '../../messages';
import { area, line, curveNatural } from 'd3-shape';
import { scaleLinear } from 'd3-scale';
import { CustomLayer, ResponsiveLine } from '@nivo/line';
import { calcFresnelRadius, radioHeight } from '../../utils/P526_10';
import {
  getLabelsByPrefs,
  calcRadioHeights,
  calculateTicks,
  getMinMaxOfSeries,
  getSightLine,
  calcPrecision,
  getXPosition,
} from '../../utils/chartUtils';
import {
  BROWN,
  BLUE,
  GRAY,
  YELLOW,
  RED,
  EARTHRADIUS,
  METER_TO_FEET,
  MIN_LINK_LENGTH_KM,
  KILOMETERS_TO_MILES,
  METERS_TO_MILES,
  METERS_TO_KILOMETERS,
} from '../../app.constants';
import { Container, Dimmer, Loader, Message } from 'semantic-ui-react';
import { Profile, fresnelMultiplier } from './Profile';
import { ProfilePoint } from './ProfilePoint';
import { RootStateOrAny } from 'src/store';
import { ClutterDetailsType } from '../../utils/types/commonTypes';
import {
  meterToAny,
  fixedPrecision,
  last,
  findIndexAtRange,
  pyround,
  km_to_length,
  pybearing,
} from '../../utils/useful_functions';
import { updateSelection } from './profile.reducer';
import { ReflectionLine } from 'src/model/Reflection';
import { bearingToAzimuth } from '@turf/helpers';

const MARGIN = { top: 10, right: 62, bottom: 50, left: 60 };

const calcTilt = (
  end: ProfilePoint,
  endHeight: number,
  highPoint: ProfilePoint,
  heightUnits = 'm',
  rangeUnits = 'km'
): number => {
  /**
   * Calculates the antenna tilt to direct at the other point from this point.
   * thisPoint and otherPoint are objects with:
   * - point: an object with a `range` property (distance between them in meters)
   * - actual_height: height above sea level including antenna or obstruction
   */
  let endRangeMeters =
    end.range / (rangeUnits === 'km' ? METERS_TO_KILOMETERS : METERS_TO_MILES);
  let highPointRangeMeters =
    highPoint.range /
    (rangeUnits === 'km' ? METERS_TO_KILOMETERS : METERS_TO_MILES);
  const ke = 4 / 3;
  const r = Math.abs(endRangeMeters - highPointRangeMeters);
  if (r < 1) {
    return 0;
  }

  const curvature = -r / (2 * EARTHRADIUS * ke);
  let hpHeight = highPoint.totalHeight;
  let endPointHeight = endHeight;
  // always the diff should be in meters so
  // if heightunits are ft we need to convert to
  // meters
  if (heightUnits === 'ft') {
    endPointHeight = endPointHeight / METER_TO_FEET;
    hpHeight = hpHeight / METER_TO_FEET;
  }
  let diff = hpHeight - endPointHeight;
  const angle = Math.atan2(diff, r) + curvature;
  return angle * (180 / Math.PI); // convert radians to degrees
};

const calcAzimuth = (start, end): number => {
  const lineBearing = bearingToAzimuth(
    pybearing(start[0], start[1], end[0], end[1])
  );
  return lineBearing;
};

/*
 * Backend schema when requesting/editing a profile
 */
export type ProfileResponse = {
  loc_lat: number;
  loc_lng: number;
  rem_lat: number;
  rem_lng: number;
  ranges: number[];
  heights: number[];
  clutter_types: string[] | null;
  obstructions: number[];
  use_clutter: boolean;
  is_lidar: boolean;
  // This isn't part of the response from the backend
  // but it is used to handle errors in the request
  errMsg: string | null;
  // Used to indicate if the UI is waiting for the response
  loading: boolean;
};

export type ProfileChartProps = {
  profileData: ProfileResponse;
  localHeight: number;
  remoteHeight: number;
  loading: boolean;
  frequencyGHz: number;
  rangeUnits: string;
  heightUnits: string;
  localSiteName: string;
  remoteSiteName: string;
  isEditing: boolean;
  reflectionLine?: ReflectionLine;
  modified?: boolean;
  setModified?: Function;
};

/*
 * Return the title for the path profile
 *
 * PathLength: float in metres
 * profileType: String, e.g. "Line-of-Sight", "Near Line-of-Sight"
 * prefs: prefs object
 */
export const getProfileTitle = (
  pathLength: number,
  profileType: string,
  prefs: any
) => {
  const displayLength = pyround(km_to_length(pathLength / 1000), 3);
  const lengthUnits = prefs.rangeUnits === 'km' ? 'kilometers' : 'miles';
  return `Profile : ${displayLength} ${lengthUnits}, ${profileType}`;
};

export const profileInterpolation = (
  ranges: number[],
  heights: number[],
  clutter_types: string[],
  obstructions: number[],
  clutterDetails: any,
  highPoint: boolean = false
) => {
  if (!ranges || ranges.length === 0) {
    // when the data is empty, return the original values
    return { ranges, heights, clutter_types, obstructions };
  }

  const clutterLookupTable = Object.fromEntries(
    clutterDetails.map((c) => [c.key, c.details[1]])
  );

  const interpRanges: number[] = [];
  const interpHeights: number[] = [];
  const interpClutterTypes: string[] = [];
  const interpObstructions: number[] = [];
  interpRanges.push(ranges[0]);
  interpHeights.push(heights[0]);
  if (highPoint) {
    interpObstructions.push(0);
    interpClutterTypes.push('U');
  } else {
    // intermediate points calculation for clutter
    interpClutterTypes.push(clutter_types[0]);
    interpObstructions.push(obstructions[0]);
  }

  const lastIndex = heights.length - 2;
  heights.forEach((item, index) => {
    if (heights[index + 1] != null) {
      const clutterHeight = clutterLookupTable[clutter_types[index]];
      const nxtClutterHeight = clutterLookupTable[clutter_types[index + 1]];
      interpHeights.push((heights[index] + heights[index + 1]) / 2);
      interpRanges.push((ranges[index] + ranges[index + 1]) / 2);
      if (highPoint && index === 0) {
        interpObstructions.push(obstructions[index + 1]);
        interpClutterTypes.push(clutter_types[index + 1]);
      } else if (highPoint && index === lastIndex) {
        interpObstructions.push(obstructions[index]);
        interpClutterTypes.push(clutter_types[index]);
      } else if (
        clutterHeight + obstructions[index] >
        nxtClutterHeight + obstructions[index + 1]
      ) {
        interpObstructions.push(obstructions[index]);
        interpClutterTypes.push(clutter_types[index]);
      } else {
        interpObstructions.push(obstructions[index + 1]);
        interpClutterTypes.push(clutter_types[index + 1]);
      }

      interpHeights.push(heights[index + 1]);
      interpRanges.push(ranges[index + 1]);
      if (highPoint && index === lastIndex) {
        interpClutterTypes.push('U');
        interpObstructions.push(0);
      } else {
        interpClutterTypes.push(clutter_types[index + 1]);
        interpObstructions.push(obstructions[index + 1]);
      }
    }
  });

  return {
    ranges: interpRanges,
    heights: interpHeights,
    clutter_types: interpClutterTypes,
    obstructions: interpObstructions,
  };
};

type ProfileType = 'Line-of-Sight' | 'Near Line-of-Sight' | 'Non Line-of-Sight';

/*
 * Return the profile type as a Line-of-Sight string
  this function is called outside of this component and
  always expects the heights and ranges in meters
 */
export const getProfileType = (
  localHeight: number,
  remoteHeight: number,
  frequencyGHz: number,
  profileData: ProfileResponse,
  clutterDetails: any
): ProfileType => {
  if (!profileData) {
    return '';
  }

  let { ranges, heights, clutter_types, obstructions, use_clutter } =
    profileData;
  if (use_clutter) {
    const interpolatedProfile = profileInterpolation(
      ranges,
      heights,
      clutter_types,
      obstructions,
      clutterDetails
    );
    ranges = interpolatedProfile.ranges;
    heights = interpolatedProfile.heights;
    clutter_types = interpolatedProfile.clutter_types;
    obstructions = interpolatedProfile.obstructions;
  }

  const clutterLookupTable = Object.fromEntries(
    clutterDetails.map((c) => [c.key, c.details[1]])
  );
  const pathLength = last(ranges);
  const fzMultiplier = fresnelMultiplier(use_clutter);
  // Local and remote antenna heights above sea level
  const localASL = localHeight + heights[0];
  const remoteASL = remoteHeight + last(heights);
  let nearLOS = false;

  // Ignore the first and last points when considering the
  // profile type
  for (let idx = 1; idx <= ranges.length - 2; idx++) {
    const currentRange = ranges[idx];
    const correctionFactor = radioHeight(pathLength, currentRange);
    let totalHeight = heights[idx] + obstructions[idx] + correctionFactor;
    if (use_clutter) {
      // S and T (lidar clutter) are not in the clutter lookup table
      // so default to 0
      const clutterHeight = clutterLookupTable[clutter_types[idx]] || 0;
      totalHeight += clutterHeight;
    }
    const fresnelRadius =
      fzMultiplier * calcFresnelRadius(frequencyGHz, currentRange, pathLength);
    const sight = getSightLine({
      xpos: currentRange,
      lAntennaHeight: localASL,
      rAntennaHeight: remoteASL,
      pathLength: pathLength,
    });
    if (sight - totalHeight < 0) {
      // if any point is non-LOS, abort early
      return 'Non Line-of-Sight';
    } else if (sight - fresnelRadius - totalHeight < 0) {
      // the terrain obstructs the Fresnel Radius
      nearLOS = true;
      // continue processing for near-LOS since another point could be non-LOS
    }
  }

  if (nearLOS) {
    return 'Near Line-of-Sight';
  }
  // If we made it this far then the profile must be line-of-sight
  return 'Line-of-Sight';
};

const setChartX = (dispatch, ranges, startIndex, xpos, reset = false) => {
  const index = findIndexAtRange(xpos, ranges);
  if (reset) {
    dispatch(
      updateSelection({
        startIndex: index,
        endIndex: index,
        target: 'chart',
      })
    );
  } else {
    dispatch(
      updateSelection({
        startIndex: startIndex,
        endIndex: index,
        target: 'chart',
      })
    );
  }
};

const delayedSetChartX = throttle(
  (dispatch, ranges, startIndex, clickedRange) => {
    setChartX(dispatch, ranges, startIndex, clickedRange);
  },
  200
);

const getCurrentRange = (event, ranges) => {
  const { target } = event;
  const width = target.getAttribute('width');
  const xPosition = getXPosition(event);
  const scale = scaleLinear()
    .domain([0, width])
    .range([ranges[0], last(ranges)]);
  return scale(xPosition);
};

export type ChartPoint = {
  x: number;
  y: number;
};

export type AreaPoint = {
  x: number;
  height: number; // resembles y low values
  y: number; // resembles y high values
};

function ProfileChart(props: ProfileChartProps) {
  const {
    profileData,
    loading,
    frequencyGHz = 5.8,
    localSiteName,
    remoteSiteName,
    isEditing = false,
    reflectionLine,
  } = props;
  if (!profileData) {
    // nothing to draw
    return <Loader active />;
  }
  // Get units from Redux
  const heightUnits = useSelector(
    (state: RootStateOrAny) => state.mainFrame.prefs.heightUnits
  );
  const rangeUnits = useSelector(
    (state: RootStateOrAny) => state.mainFrame.prefs.rangeUnits
  );

  const convertedProfileData = {
    ...profileData,
    ranges: profileData.ranges.map((val) =>
      meterToAny(val, rangeUnits, 1, false)
    ),
    localHeight: meterToAny(props.localHeight, heightUnits, 1, false),
    remoteHeight: meterToAny(props.remoteHeight, heightUnits, 1, false),
    heights: profileData.heights.map((val) =>
      meterToAny(val, heightUnits, 1, false)
    ),
    obstructions: profileData.obstructions.map((val) =>
      meterToAny(val, heightUnits, 1, false)
    ),
  };

  // Use convertedProfileData throughout the component
  const {
    ranges,
    loc_lat,
    loc_lng,
    rem_lat,
    rem_lng,
    heights,
    clutter_types,
    obstructions,
    use_clutter,
    errMsg,
    is_lidar,
    localHeight,
    remoteHeight,
  } = convertedProfileData;

  const { formatMessage } = useIntl();
  const clutterDetailsRaw = useSelector(
    (state: RootStateOrAny) => state.mainFrame.clutterDetails
  );
  // Convert clutter heights to feet if needed
  const clutterDetails =
    heightUnits === 'ft'
      ? clutterDetailsRaw.map((c) => ({
          ...c,
          details: [c.details[0], c.details[1] * METER_TO_FEET],
        }))
      : clutterDetailsRaw;
  const sidebarResizing = useSelector(
    (state: RootStateOrAny) => state.mainFrame.sidebarResizing
  );
  const { startIndex, endIndex } = useSelector(
    (state: RootStateOrAny) => state.profile
  );

  const isMouseDown = useRef(false);

  const dispatch = useDispatch();
  const isValidData = () => {
    let validData = true;
    if (ranges.length !== heights.length) {
      validData = false;
    } else if (use_clutter && ranges.length !== clutter_types.length) {
      validData = false;
    } else if (
      obstructions.length > 0 &&
      obstructions.length !== ranges.length
    ) {
      validData = false;
    }
    return validData;
  };

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader inverted>Loading</Loader>
      </Dimmer>
    );
  } else if (errMsg) {
    return (
      <Message negative size="mini">
        <p>{errMsg}</p>
      </Message>
    );
  } else if (!isValidData()) {
    return (
      <Message negative size="mini">
        <p>{formatMessage(additionalMessages.invalidData)}</p>
      </Message>
    );
  } else {
    const pathLength = last(ranges);
    // MIN_LINK_LENGTH_M is in meters, so convert to km or mi for comparison
    let minLinkLength = MIN_LINK_LENGTH_KM; // default km
    if (rangeUnits === 'mi') {
      minLinkLength = MIN_LINK_LENGTH_KM * KILOMETERS_TO_MILES;
    }
    if (pathLength < minLinkLength) {
      return (
        <Container
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <Message negative size="large">
            <FormattedMessage
              id="profile.tooShort"
              defaultMessage="Path is too short"
            />
          </Message>
        </Container>
      );
    }
    const labels = getLabelsByPrefs(
      { rangeUnits: rangeUnits, heightUnits: heightUnits },
      formatMessage
    );
    const gridLineCoordsArr: { id: string; color: string; data: any[] }[] = [];
    const profile = new Profile({
      heights,
      ranges,
      clutterTypes: clutter_types,
      obstructions,
      use_clutter,
      localHeight,
      remoteHeight,
      frequencyGHz,
      clutterDetails,
      is_lidar,
      heightUnits,
      rangeUnits,
    });
    profile.calculateHighPoints();
    const xAxisPrecision = calcPrecision(profile.pathLength);
    if (use_clutter) {
      profile.interpolatedProfile = profile.calculateInterpolatedProfile();
    }
    const [fresnelData, sightLineData] = getFresnelAndSightLineData(
      profile,
      heightUnits,
      rangeUnits
    );
    const clutterData = getClutterData(profile, clutterDetails);
    const [obstructionData, worstEarthData] = getObsAndWorstEarthData(profile);
    const heightsData = getTerrainData(profile);

    const obstructionSeries = {
      data: obstructionData,
      color: YELLOW,
      label: formatMessage(additionalMessages.obstruction),
    };
    const worstEarthSeries = {
      data: worstEarthData,
      color: GRAY,
      label: formatMessage(additionalMessages.worstEarth),
    };
    // Custom clutter layer
    const clutterLayer = ({ xScale, yScale }: any) => {
      const areaGenerator = area()
        .x((bar) => {
          return xScale(bar['x']);
        })
        .y0((bar) => yScale(bar['height']))
        .y1((bar, i) => yScale(bar['y']));

      return clutterData.map((clutter, i) => (
        <path
          id={'clutter' + i}
          key={'clutter' + i}
          d={areaGenerator(clutter.data)!}
          fill={clutter.color}
        />
      ));
    };

    // Y axis line calculation
    const flatClutterData = flatten(clutterData.map((obj) => obj.data));

    let [yMin, yMax]: number[] = getMinMaxOfSeries([
      ...fresnelData,
      ...heightsData,
      ...obstructionData,
      ...flatClutterData,
      ...worstEarthData,
    ]);
    const xTicks = calculateTicks([ranges[0], last(ranges)]);
    if (last(xTicks) !== pathLength) {
      xTicks.push(pathLength);
    }
    const yTicks = calculateTicks([yMin, yMax], true);
    const yOffsets = calcRadioHeights(ranges, rangeUnits, heightUnits);
    let floatValueYTicks = false;
    const gridMax = last(yTicks) + (yTicks[1] - yTicks[0]) / 2;
    yTicks.forEach((yTick, i) => {
      if (!Number.isInteger(yTick)) {
        floatValueYTicks = true;
      }
      let gridLineCoords: any[] = [];
      xTicks.forEach((xTick, j) => {
        // Prevent the grid lines from going above
        // the chart area
        if (yTick + yOffsets[j] > gridMax) {
          gridLineCoordsArr.push({
            id: `Grid Line ${i}`,
            color: '#E1E1E1',
            data: gridLineCoords,
          });
          gridLineCoords = [];
        } else {
          gridLineCoords.push({
            x: xTick,
            y: yTick + yOffsets[j],
          });
        }
      });
      gridLineCoordsArr.push({
        id: `Grid Line ${i}`,
        color: '#E1E1E1',
        data: gridLineCoords,
      });
    });
    const yAxisTickFormatter = (value) => {
      if (floatValueYTicks) {
        return value.toFixed(1);
      } else {
        return value;
      }
    };

    // Custom y axis line layer
    const gridLineLayer: CustomLayer = ({ xScale, yScale }: any) => {
      const lineGenerator = line()
        .x((bar) => xScale(bar['x']))
        .y((bar) => yScale(bar['y']))
        .curve(curveNatural);
      return gridLineCoordsArr.map((gridLine, index) => (
        <path
          key={index}
          d={lineGenerator(gridLine.data)!}
          stroke="#EAEAEA"
          fill="none"
          strokeWidth={1.5}
        />
      ));
    };

    // Obstruction layer
    const obstructionLayer: CustomLayer = ({ xScale, yScale }: any) => {
      const areaGenerator = area<AreaPoint>()
        .x((bar) => {
          return xScale(bar['x']);
        })
        .y0((bar) => yScale(bar['height']))
        .y1((bar, i) => {
          return yScale(bar['y']);
        });

      return (
        <path
          id="obstructions"
          d={areaGenerator(obstructionSeries.data)!}
          fill={obstructionSeries.color}
        />
      );
    };

    // fresnel layer
    const fresnelLayer: CustomLayer = ({ xScale, yScale }: any) => {
      const lineGenerator = line<ChartPoint>()
        .x((bar) => xScale(bar['x']))
        .y((bar) => yScale(bar['y']))
        .curve(curveNatural);
      return (
        <path
          d={lineGenerator(fresnelData)!}
          stroke={BLUE}
          fill="rgba(0,0,255,0.3)"
          strokeWidth={1}
        />
      );
    };

    // sight line layer
    const sightLineLayer: CustomLayer = ({ xScale, yScale }: any) => {
      const lineGenerator = line<ChartPoint>()
        .x((bar) => xScale(bar['x']))
        .y((bar) => yScale(bar['y']))
        .curve(curveNatural);
      return (
        <path
          id="sightline"
          d={lineGenerator(sightLineData)!}
          stroke={GRAY}
          fill="none"
          strokeWidth={2}
        />
      );
    };

    // worst earth layer
    const worstEarthLayer: CustomLayer = ({ xScale, yScale }: any) => {
      const lineGenerator = line<ChartPoint>()
        .x((bar) => xScale(bar['x']))
        .y((bar) => yScale(bar['y']));
      return (
        <path
          id="worstearth"
          d={lineGenerator(worstEarthSeries.data)!}
          stroke={worstEarthSeries.color}
          fill="none"
          strokeWidth={2}
        />
      );
    };

    //selection layer
    let selectionLayer: CustomLayer = () => [];
    if (isEditing && startIndex !== null && endIndex !== null) {
      let startIdx = Math.min(startIndex, endIndex);
      let endIdx = Math.max(startIndex, endIndex);
      const start_x =
        startIdx === 0
          ? ranges[startIdx]
          : // use the mid point
            (ranges[startIdx] + ranges[startIdx - 1]) / 2.0;
      const end_x =
        endIdx === ranges.length - 1
          ? ranges[endIdx]
          : (ranges[endIdx] + ranges[endIdx + 1]) / 2.0;
      const selectionLayerData = [
        { x: start_x, y: yMin },
        { x: start_x, y: yMax },
        { x: end_x, y: yMax },
        { x: end_x, y: yMin },
      ];
      selectionLayer = ({ xScale, yScale }: any) => {
        const lineGenerator = line<ChartPoint>()
          .x((bar) => xScale(bar.x))
          .y((bar) => yScale(bar.y));
        return (
          <path
            d={lineGenerator(selectionLayerData)!}
            fill={GRAY}
            opacity={0.5}
          />
        );
      };
    }

    function getReflectionCustomLayers(reflectionLine: ReflectionLine) {
      if (reflectionLine === null || reflectionLine === undefined) {
        return [];
      }

      const {
        greyReflection: greyReflectionMts,
        blueReflection: blueReflectionMts,
      } = reflectionLine;

      if (greyReflectionMts === undefined || greyReflectionMts === undefined) {
        return [];
      }
      const greyReflection: ChartPoint[] = greyReflectionMts.map(
        ({ x, y }) => ({
          x: meterToAny(x, rangeUnits, 1, false),
          y: Math.max(meterToAny(y, heightUnits, 1, false), yMin),
        })
      );
      const blueReflection: ChartPoint[] = blueReflectionMts.map(
        ({ x, y }) => ({
          // to avoid -ve values and line get plotted below x axis
          x: meterToAny(x, rangeUnits, 1, false),
          y: Math.max(meterToAny(y, heightUnits, 1, false), yMin),
        })
      );
      const blueReflectionLength = blueReflection.length;
      const sightLineDataLength = sightLineData.length;

      //reflection layer -- grey line
      const reflectionGreyLineLayer: CustomLayer = ({
        xScale,
        yScale,
      }: any) => {
        const lineGenerator = line<ChartPoint>()
          .x((bar) => xScale(bar['x']))
          .y((bar) => yScale(bar['y']));
        return (
          <path
            id="reflectionGreyLineLayer"
            d={lineGenerator(greyReflection)!}
            stroke={GRAY}
            fill="none"
            strokeWidth={2}
          />
        );
      };

      //reflection layer -- blue line
      const reflectionBlueLineLayer: CustomLayer = ({
        xScale,
        yScale,
      }: any) => {
        const lineGenerator = line<ChartPoint>()
          .x((bar) => xScale(bar['x']))
          .y((bar) => yScale(bar['y']));
        return (
          <path
            id="reflectionBlueLineLayer"
            d={lineGenerator(blueReflection)!}
            stroke={BLUE}
            fill="none"
            strokeWidth={4}
          />
        );
      };

      // reflection layer -- local antenna to blue line left
      const reflectionLocalToBlueLineLeftLayer: CustomLayer = ({
        xScale,
        yScale,
      }: any) => {
        const lineGenerator = line<ChartPoint>()
          .x((bar) => xScale(bar['x']))
          .y((bar) => yScale(bar['y']));
        return (
          <path
            id="reflectionLocalToBlueLineLeftLayer"
            // if blueReflection array is empty, then passing sightLineData, so that it will be on same point
            d={lineGenerator([
              sightLineData[0],
              blueReflectionLength ? blueReflection[0] : sightLineData[0],
            ])}
            stroke={BLUE}
            fill="none"
            strokeWidth={2}
          />
        );
      };

      // reflection layer -- local antenna to blue line right
      const reflectionLocalToBlueLineRightLayer: CustomLayer = ({
        xScale,
        yScale,
      }: any) => {
        const lineGenerator = line<ChartPoint>()
          .x((bar) => xScale(bar['x']))
          .y((bar) => yScale(bar['y']));
        return (
          <path
            id="reflectionLocalToBlueLineRightLayer"
            d={
              lineGenerator([
                sightLineData[0],
                blueReflectionLength
                  ? blueReflection[blueReflectionLength - 1]
                  : sightLineData[0],
              ])!
            }
            stroke={BLUE}
            fill="none"
            strokeWidth={2}
          />
        );
      };

      // reflection layer -- remote antenna to blue line left
      const reflectionRemoteToBlueLineLeftLayer: CustomLayer = ({
        xScale,
        yScale,
      }: any) => {
        const lineGenerator = line<ChartPoint>()
          .x((bar) => xScale(bar['x']))
          .y((bar) => yScale(bar['y']));
        return (
          <path
            id="reflectionRemoteToBlueLineLeftLayer"
            d={
              lineGenerator([
                sightLineData[sightLineDataLength - 1],
                blueReflectionLength
                  ? blueReflection[0]
                  : sightLineData[sightLineDataLength - 1],
              ])!
            }
            stroke={BLUE}
            fill="none"
            strokeWidth={2}
          />
        );
      };

      // reflection layer -- remote antenna to blue line right
      const reflectionRemoteToBlueLineRightLayer: CustomLayer = ({
        xScale,
        yScale,
      }: any) => {
        const lineGenerator = line<ChartPoint>()
          .x((bar) => xScale(bar['x']))
          .y((bar) => yScale(bar['y']));
        return (
          <path
            id="reflectionRemoteToBlueLineRightLayer"
            d={
              lineGenerator([
                sightLineData[sightLineDataLength - 1],
                blueReflectionLength
                  ? blueReflection[blueReflectionLength - 1]
                  : sightLineData[sightLineDataLength - 1],
              ])!
            }
            stroke={BLUE}
            fill="none"
            strokeWidth={2}
          />
        );
      };

      return [
        reflectionGreyLineLayer,
        reflectionBlueLineLayer,
        reflectionLocalToBlueLineLeftLayer,
        reflectionLocalToBlueLineRightLayer,
        reflectionRemoteToBlueLineLeftLayer,
        reflectionRemoteToBlueLineRightLayer,
      ];
    }

    const reflectionCustomLayers = getReflectionCustomLayers(reflectionLine);

    let customLayers: CustomLayer[] = [
      worstEarthLayer,
      gridLineLayer,
      obstructionLayer,
      clutterLayer,
      sightLineLayer,
      fresnelLayer,
      selectionLayer,
    ];

    // non los sight line layer
    let noLosLineLayer: CustomLayer;
    if (profile.highPoints.length > 2) {
      const highPointsData = {
        data: [] as ChartPoint[],
        color: RED,
      };
      profile.highPoints.forEach((hp: ProfilePoint) => {
        highPointsData.data.push({ x: hp.range, y: hp.totalHeight });
      });
      noLosLineLayer = ({ xScale, yScale }: any) => {
        const lineGenerator = line<ChartPoint>()
          .x((bar) => xScale(bar.x))
          .y((bar) => yScale(bar.y));
        return (
          <path
            d={lineGenerator(highPointsData.data)!}
            stroke={highPointsData.color}
            fill="none"
            strokeWidth={2}
          />
        );
      };
      customLayers.splice(3, 0, noLosLineLayer);
    }

    const chartData = [
      {
        id: formatMessage(additionalMessages.groundHeight),
        color: BROWN,
        data: heightsData,
      },
    ];

    const localPoint = profile.points[0];
    const localTilt = Math.max(
      ...profile.highPoints.slice(1).map((hp) => {
        return calcTilt(
          localPoint,
          localPoint.height + profile.localHeight,
          hp,
          heightUnits,
          rangeUnits
        );
      })
    );

    const remotePoint = profile.points[profile.points.length - 1];
    const remoteTilt = Math.max(
      ...profile.highPoints
        .slice(0, profile.highPoints.length - 1)
        .map((hp) => {
          return calcTilt(
            remotePoint,
            remotePoint.height + profile.remoteHeight,
            hp,
            heightUnits,
            rangeUnits
          );
        })
    );

    const localAzimuth = calcAzimuth([loc_lat, loc_lng], [rem_lat, rem_lng]);
    const remoteAzimuth = calcAzimuth([rem_lat, rem_lng], [loc_lat, loc_lng]);

    const isObstructedStyle =
      profile.highPoints.length > 2 ? { color: 'red' } : {};

    return (
      <div className="path-profile-chart">
        <div className="path-profile-site-info">
          <div className="site-label-info">
            <p className="local-site-name">{localSiteName}</p>
            <div className="site-label-bottom-info">
              <p>
                <span className="label">Az:</span>
                {pyround(localAzimuth, 1)}°
              </p>
              <p style={isObstructedStyle}>
                <span className="label">Tilt:</span>
                {pyround(localTilt, 1)}° (
                {localTilt >= 0 ? 'uptilt' : 'downtilt'})
              </p>
            </div>
          </div>

          <div className="site-label-info">
            <p className="remote-site-name">{remoteSiteName}</p>
            <div className="site-label-bottom-info">
              <p>
                <span className="label">Az:</span>
                {pyround(remoteAzimuth, 1)}°
              </p>
              <p style={isObstructedStyle}>
                <span className="label">Tilt:</span>
                {pyround(remoteTilt, 1)}° (
                {remoteTilt >= 0 ? 'uptilt' : 'downtilt'})
              </p>
            </div>
          </div>
        </div>
        {sidebarResizing ? null : (
          <ResponsiveLine
            enableGridY={false}
            enableArea={true}
            // Add 'slices' layer for visibility of tooltip
            layers={[
              'grid',
              'axes',
              //'slices',
              ...customLayers,
              ...reflectionCustomLayers,
              'lines',
              'areas',
              'mesh',
            ]}
            colors={{ datum: 'color' }}
            areaBaselineValue={yMin}
            data={chartData}
            margin={{ ...MARGIN }}
            xScale={{ type: 'linear', min: 'auto', max: 'auto' }}
            yScale={{
              type: 'linear',
              min: yMin,
              max: last(yTicks),
              stacked: false,
            }}
            axisTop={null}
            onClick={
              isEditing
                ? (a, event) => {
                    if (isMouseDown.current) {
                      // this click event is for mouseup
                      isMouseDown.current = false;
                    } else {
                      // this is a mouse down event
                      const clickedRange = getCurrentRange(event, ranges);
                      setChartX(
                        dispatch,
                        ranges,
                        startIndex,
                        clickedRange,
                        true
                      );
                    }
                  }
                : null
            }
            onMouseMove={
              isEditing
                ? (point, event) => {
                    const { buttons } = event;
                    if (buttons === 1) {
                      const clickedRange = getCurrentRange(event, ranges);
                      if (isMouseDown.current) {
                        // user is dragging
                        delayedSetChartX(
                          dispatch,
                          ranges,
                          startIndex,
                          clickedRange
                        );
                      } else {
                        // register this as a click event and start dragging
                        isMouseDown.current = true;
                        setChartX(
                          dispatch,
                          ranges,
                          startIndex,
                          clickedRange,
                          true
                        );
                      }
                    }
                  }
                : null
            }
            enablePoints={false}
            axisBottom={{
              format: function (value: any) {
                return fixedPrecision(value, xAxisPrecision);
              },
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: formatMessage(additionalMessages.rangeLabel, {
                unit: labels['lengthLbl'],
              }),
              legendOffset: 36,
              legendPosition: 'middle',
            }}
            axisLeft={{
              format: function (value: any) {
                return yAxisTickFormatter(value);
              },
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: `${formatMessage(additionalMessages.heightLabel, {
                unit: labels['heightLbl'],
              })}`, // - ${localPlace}`,
              legendOffset: -55,
              legendPosition: 'middle',
            }}
            axisRight={{
              format: function (value: any) {
                return yAxisTickFormatter(value);
              },
              tickRotation: 0,
              legend: `${formatMessage(additionalMessages.heightLabel, {
                unit: labels['heightLbl'],
              })}`, // - ${remotePlace}`,
              legendOffset: 55,
              legendPosition: 'middle',
            }}
            pointSize={3}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={1}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            useMesh={true}
            legends={[
              {
                anchor: 'bottom',
                direction: 'row',
                justify: false,
                translateY: 80,
                itemsSpacing: 0,
                itemWidth: 73,
                itemHeight: 20,
                itemOpacity: 0.75,
                symbolSize: 12,
                symbolShape: 'circle',
                symbolBorderColor: 'rgba(0, 0, 0, .5)',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemBackground: 'rgba(0, 0, 0, .03)',
                      itemOpacity: 1,
                    },
                  },
                ],
              },
            ]}
            animate={false}
            tooltip={() => <></>} //disabled default tooltip
          />
        )}
      </div>
    );
  }
}

export function getFresnelAndSightLineData(
  profile: Profile,
  heightUnits: 'ft' | 'm' = 'm',
  rangeUnits: 'km' | 'mi' = 'km'
): Array<ChartPoint[]> {
  // Fresnel calculation
  const numPoints = 40;
  const fresUpPoints: ChartPoint[] = [];
  const fresLowPoints: ChartPoint[] = [];
  let fresnelData: ChartPoint[] = [];
  const sightLine: ChartPoint[] = [];
  // Convert pathLength to meters if needed
  const pathLength = profile.pathLength;
  let pathLengthMeters;
  if (rangeUnits === 'mi') {
    pathLengthMeters = pathLength / METERS_TO_MILES; // convert miles to meters
  } else if (rangeUnits === 'km') {
    pathLengthMeters = pathLength / METERS_TO_KILOMETERS; // convert km to meters
  }
  const lAntennaHeight = profile.localHeight + profile.heights[0] || 0; //+ groundAtAP
  const rAntennaHeight = profile.remoteHeight + last(profile.heights) || 0; //+ groundAtSM
  for (let r = 0; r <= numPoints; r++) {
    const xpos =
      (pathLength * (1 - Math.cos((Math.PI * r) / numPoints + 0.00001))) / 2;
    const sightY = getSightLine({
      xpos,
      lAntennaHeight,
      rAntennaHeight,
      pathLength,
    });
    const xposMeters =
      rangeUnits === 'mi'
        ? xpos / METERS_TO_MILES
        : xpos / METERS_TO_KILOMETERS; // convert to meters
    // Fresnel radius calculation
    const fresnelRadius =
      profile.fresMultiplier *
      calcFresnelRadius(profile.frequencyGHz, xposMeters, pathLengthMeters) *
      (heightUnits === 'ft' ? METER_TO_FEET : 1);
    const lowerFresnel = sightY - fresnelRadius;
    const upperFresnel = sightY + fresnelRadius;
    fresLowPoints.push({ x: xpos, y: lowerFresnel });
    fresUpPoints.push({ x: xpos, y: upperFresnel });
    sightLine.push({ x: xpos, y: sightY });
  }
  fresnelData = fresLowPoints.concat(fresUpPoints.reverse());
  return [fresnelData, sightLine];
}

export function getObsAndWorstEarthData(
  profile: Profile
): [AreaPoint[], ChartPoint[]] {
  if (profile.use_clutter) {
    profile = profile.interpolatedProfile!;
  }
  const { points } = profile;
  const xVals: number[] = [];
  const yLowVals: number[] = [];
  const yHighVals: number[] = [];
  const weXVals: number[] = [];
  const weYVals: number[] = [];
  const obstructionData: AreaPoint[] = [];
  const worstEarthData: ChartPoint[] = [];
  points.forEach(
    (startPoint: ProfilePoint, i, contextArray: ProfilePoint[]) => {
      if (profile.use_clutter) {
        if (i < contextArray.length - 1) {
          const endPoint: ProfilePoint = points[i + 1];
          let realPoint: ProfilePoint;
          const startX = startPoint.range;
          const endX = endPoint.range;
          if (i % 2 === 0) {
            realPoint = startPoint;
          } else {
            realPoint = endPoint;
          }
          const startY: number = startPoint.radioHeight;
          const endY: number = endPoint.radioHeight;
          const startYWe: number = startPoint.height + startPoint.worstEarth;
          const endYWe: number = endPoint.height + endPoint.worstEarth;
          const clutterHeight: number = realPoint.clutterHeight;
          const obstruction: number = realPoint.obstruction;
          if (obstruction) {
            xVals.push(startX, startX, endX, endX);
            yLowVals.push(
              startY + clutterHeight,
              startY + clutterHeight,
              endY + clutterHeight,
              endY + clutterHeight
            );
            yHighVals.push(
              startY + clutterHeight,
              startY + clutterHeight + obstruction,
              endY + clutterHeight + obstruction,
              endY + clutterHeight
            );
          }
          weXVals.push(startX, endX);
          weYVals.push(
            startYWe + clutterHeight + obstruction,
            endYWe + clutterHeight + obstruction
          );
        }
      } else {
        xVals.push(startPoint.range);
        yLowVals.push(startPoint.radioHeight);
        yHighVals.push(startPoint.radioHeight + startPoint.obstruction);
        weXVals.push(startPoint.range);
        weYVals.push(
          startPoint.height + startPoint.obstruction + startPoint.worstEarth
        );
      }
    }
  );
  xVals.forEach((xVal, i) => {
    obstructionData.push({
      x: xVal,
      y: yHighVals[i],
      height: yLowVals[i],
    });
  });
  weXVals.forEach((weXVal, i) => {
    worstEarthData.push({
      x: weXVal,
      y: weYVals[i],
    });
  });

  return [obstructionData, worstEarthData];
}

export function getClutterData(
  profile: Profile,
  clutterDetails: ClutterDetailsType
) {
  const realPoints = profile.points;
  if (profile.use_clutter) {
    profile = profile.interpolatedProfile!;
  }
  const cSeriesArr: any[] = [];
  let prevInterpolated: ProfilePoint;
  let xVals: number[] = [];
  let yLowVals: number[] = [];
  let yHighVals: number[] = [];
  let currentColor: string = '#ffffff00';
  let color: string;
  const intrpPoints = profile.points.filter((point, i) => i % 2 === 1);
  if (profile.use_clutter) {
    realPoints.forEach((real: ProfilePoint, i) => {
      let interpolated: ProfilePoint = intrpPoints[i];
      let startX = 0;
      let endX = 0;
      let startY = 0;
      let endY = 0;
      let middleX = 0;
      let middleY = 0;
      let cltStartY = 0;
      let cltEndY = 0;
      const series = {
        data: [] as { x: number; height: number; y: number }[],
        color: currentColor,
      };
      let clutterInfo = real.clutterInfo;
      const cltHeight = clutterInfo.details[1];
      if (!prevInterpolated) {
        //First point case
        startX = real.range;
        startY = real.radioHeight;
        currentColor = clutterInfo.details[0];
      } else {
        startX = prevInterpolated.range;
        startY = prevInterpolated.radioHeight;
      }
      if (!interpolated) {
        //last point case
        endX = real.range;
        endY = real.radioHeight;
      } else {
        endX = interpolated.range;
        endY = interpolated.radioHeight;
      }
      middleX = real.range;
      middleY = real.radioHeight + cltHeight;
      cltStartY = startY + cltHeight;
      cltEndY = endY + cltHeight;

      color = clutterInfo.details[0];
      if (currentColor !== color) {
        xVals.forEach((xVal, i) => {
          series.data.push({ x: xVal, height: yLowVals[i], y: yHighVals[i] });
        });
        cSeriesArr.push(series);
        xVals = [startX, startX, middleX, endX, endX];
        yLowVals = [startY, startY, real.radioHeight, endY, endY];
        yHighVals = [startY, cltStartY, middleY, cltEndY, endY];
        currentColor = color;
      } else {
        xVals.push(startX, startX, middleX, endX, endX);
        yLowVals.push(startY, startY, real.radioHeight, endY, endY);
        yHighVals.push(startY, cltStartY, middleY, cltEndY, endY);
      }
      prevInterpolated = interpolated;
    });
    const lastSeries = {
      data: [] as { x: number; height: number; y: number }[],
      color: currentColor,
    };
    xVals.forEach((xVal, i) => {
      lastSeries.data.push({ x: xVal, height: yLowVals[i], y: yHighVals[i] });
    });
    cSeriesArr.push(lastSeries);
  }
  return cSeriesArr;
}

export function getTerrainData(profile: Profile): ChartPoint[] {
  const terrainData: ChartPoint[] = [];
  if (profile.use_clutter) {
    profile = profile.interpolatedProfile!;
  }
  profile.points.forEach((point: ProfilePoint, i) => {
    terrainData.push({ x: point.range, y: point.radioHeight });
  });
  return terrainData;
}

export default ProfileChart;
